import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Wrench, ShieldCheck, MapPin, ArrowRight, Phone, Truck, Package, X, Globe } from "lucide-react";
import { Reveal, SectionHeading, SmartImage } from "../components/common";
import EnquiryForm from "../components/EnquiryForm";
import { fpvImages, fpvGallery, fpvProduct, business } from "../data/mock";

export default function FPV() {
  const [active, setActive] = useState(null);

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-24">
        <div className="absolute inset-0 z-0">
          <SmartImage src={fpvImages.hero} alt="FPV Jack Point" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/92 via-slate-900/70 to-slate-900/40" />
        </div>
        <div className="max-w-7xl mx-auto px-5 relative z-10">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/20 border border-orange-300/30 backdrop-blur-md px-4 py-1.5 text-sm font-semibold text-orange-100">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> In stock · Ships from Cobram
            </div>
            <h1 className="mt-5 font-heading text-4xl md:text-6xl font-extrabold text-white max-w-3xl leading-tight">{fpvProduct.name}</h1>
            <p className="mt-5 text-lg text-slate-200 max-w-xl">{fpvProduct.subtitle}. Hand-built to order by Fred and sold as a matched pair.</p>
            <div className="mt-6 inline-flex items-center gap-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-6 py-4">
              <div><div className="font-heading text-4xl font-bold text-white">${fpvProduct.price} <span className="text-base font-medium text-slate-300">{fpvProduct.unit}</span></div></div>
              <a href="#fpv-order" className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 transition-colors">Order now <ArrowRight size={16} /></a>
            </div>
            <div className="mt-4 flex flex-col gap-1.5 text-sm text-emerald-200">
              <span className="inline-flex items-center gap-2"><Truck size={16} /> {fpvProduct.shippingAU}</span>
              <span className="inline-flex items-center gap-2"><Globe size={16} /> {fpvProduct.shippingNZ}</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-5 grid sm:grid-cols-3 gap-5">
          {[
            { icon: Package, text: "Sold as a matched pair" },
            { icon: Truck, text: "$130 delivered Australia-wide, tracked" },
            { icon: Globe, text: "NZ shipping available at cost" },
          ].map((c) => (
            <div key={c.text} className="flex items-center gap-3">
              <c.icon size={20} className="text-orange-400 shrink-0" />
              <span className="text-slate-100 text-sm">{c.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCT */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="grid grid-cols-2 gap-4">
              <SmartImage src={fpvImages.img1} alt="FPV Jack Point cover" className="rounded-2xl h-48 md:h-60 w-full object-cover shadow-lg" />
              <SmartImage src={fpvImages.img2} alt="FPV Jack Point detail" className="rounded-2xl h-48 md:h-60 w-full object-cover shadow-lg mt-8" />
              <SmartImage src={fpvImages.img3} alt="Jack Point blank" className="rounded-2xl h-48 md:h-60 w-full object-cover shadow-lg" />
              <SmartImage src={fpvImages.img4} alt="FPV Jack Point for sale" className="rounded-2xl h-48 md:h-60 w-full object-cover shadow-lg mt-8" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="inline-block text-sm font-semibold tracking-wider uppercase text-orange-600 mb-3">The Product</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900">A clean, factory finish for your jacking points.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{fpvProduct.blurb}</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {fpvProduct.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-slate-700"><span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-orange-100 text-orange-600 shrink-0"><Check size={13} /></span><span className="text-sm">{f}</span></li>
              ))}
            </ul>
            <div className="mt-8 flex items-center gap-4">
              <div className="rounded-2xl bg-slate-900 text-white px-6 py-4"><div className="text-xs text-slate-400 uppercase">Price</div><div className="font-heading text-2xl font-bold">${fpvProduct.price} <span className="text-sm font-medium text-slate-400">{fpvProduct.unit}</span></div></div>
              <a href="#fpv-order" className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3.5 shadow-lg shadow-orange-500/30 transition-all hover:-translate-y-0.5">Order a pair <ArrowRight size={16} /></a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5">
          <SectionHeading eyebrow="Up Close" title="A closer look at the Jack Point." subtitle="Clean, tidy and built to last. Tap any photo to open." />
          <div className="mt-10 columns-2 md:columns-3 gap-4 [column-fill:_balance]">
            {fpvGallery.map((g, i) => (
              <Reveal key={i} delay={(i % 3) * 0.05} className="mb-4 break-inside-avoid">
                <motion.button whileHover={{ scale: 1.02 }} onClick={() => setActive(g)}
                  className="group relative block w-full overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-shadow bg-slate-100">
                  <SmartImage src={g.src} alt={g.alt} className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/10 transition-colors" />
                </motion.button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 grid md:grid-cols-3 gap-6">
          {[
            { icon: Wrench, title: "Hand-built to order", desc: "Each pair is made individually — not mass-produced. Real care goes into every one." },
            { icon: ShieldCheck, title: "Precision fit", desc: "Designed specifically for FPV jacking points for a clean, secure, factory-look finish." },
            { icon: MapPin, title: "Aussie-made, locally", desc: "Built right here in Cobram by a genuine Ford Performance enthusiast." },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl bg-white border border-slate-200 p-7 shadow-sm">
                <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-orange-50 text-orange-600"><c.icon size={24} /></span>
                <h3 className="mt-5 font-heading font-bold text-lg text-slate-900">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ORDER */}
      <section id="fpv-order" className="py-20 md:py-24 bg-slate-50 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-5">
          <SectionHeading eyebrow="Order / Enquire" title="Get your pair of FPV Jack Point Covers." subtitle="$130 a pair delivered Australia-wide with tracking. Shipping to New Zealand available at the appropriate postage cost — just let Fred know." />
          <div className="mt-10">
            <EnquiryForm
              type="fpv"
              cta="Send Order Enquiry"
              extraFields={[
                { name: "model", label: "Your FPV model", type: "select", options: fpvProduct.models },
                { name: "quantity", label: "How many pairs?", placeholder: "e.g. 1 pair" },
                { name: "shipTo", label: "Ship to", type: "select", options: ["Australia", "New Zealand"] },
              ]}
            />
          </div>
          <p className="mt-6 text-center text-slate-500">Prefer to order by phone? <a href={business.phoneHref} className="inline-flex items-center gap-1 text-orange-600 font-semibold"><Phone size={15} /> {business.phone}</a></p>
        </div>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {active && (
          <motion.div className="fixed inset-0 z-[60] bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-5"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setActive(null)}>
            <button className="absolute top-5 right-5 text-white/80 hover:text-white" onClick={() => setActive(null)}><X size={32} /></button>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <SmartImage src={active.src} alt={active.alt} className="w-full max-h-[80vh] object-contain rounded-xl" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
